import {
  ProcessFlowchartNodeDetailsType,
  ProcessExecutionType,
  ProcessTaskStatusType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getLogicNodeMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/logicNodeMetadata';
import {
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  ProcessFlowchartNodeType,
  STANDARD_ROOT_NODE_ICON,
  STANDARD_END_NODE_ICON,
  type StaticTemplateFlowchart,
  TASK_SCHEMA_NODE_ICON,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import type {
  EndStaticNodeData,
  StaticFlowchartNodeData,
  TaskDefinitionVersionSpecWithTaskSchema,
  TaskSchemaStaticNodeData,
  RouterStaticNodeData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import type {FlowchartConfigWithMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/flowchartConfigAutosaveManagerCallbacks';
import type {FlowchartConfigMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/reduceFlowchartConfig';
import {
  IS_FLOWCHART_MODIFIABLE_AFTER_CREATION_DEFAULT,
  IS_FLOWCHART_MODIFIABLE_ON_CREATION_DEFAULT,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/reduceFlowchartConfig';

import {ProcessFlowchartStructureQuery} from './__generated/ProcessFlowchartStructureQuery';
import {ProcessTaskGroupFlowchartDataQuery} from './__generated/ProcessTaskGroupFlowchartDataQuery';
import type {
  ReadOnlyProcessTaskDefinitionVersion,
  TaskSchemaViewerNodeData,
  ViewerStaticFlowchartNodeData,
  ViewerStaticTemplateFlowchart,
} from './ReadOnlyFlowchartTypes';

type FlowchartStructure = ExtractByTypename<
  ProcessFlowchartStructureQuery['processFlowcharts'][number],
  'ProcessFlowchartNode'
>;

type NodeConfig = FlowchartStructure['nodeConfigs'][number];

type FlowchartProcess = ExtractByTypename<ProcessTaskGroupFlowchartDataQuery['process'], 'ProcessNode'>;

export interface DisplayOnlyFlowchartResult {
  flowchart: ViewerStaticTemplateFlowchart;
  processIdsByNodeConfigId: Record<string, string>;
}

export function buildDraftForViewer(
  staticFlowchart: StaticTemplateFlowchart,
  flowchartTypeName: string
): FlowchartConfigWithMetadata {
  const metadata: FlowchartConfigMetadata = {
    transitions: {},
    rootTaskSchema: null,
    interactionState: {
      selectedNodeIds: new Set(),
      selectedEdgeIds: new Set(),
      selectedRowIds: new Set(),
      edgeLabelsByEdgeId: {},
    },
    currentVersionId: null,
    didInitialFlowchartHaveChainedRouters: false,
    flowchartTypeName,
    options: {
      isFlowchartModifiableOnCreation: IS_FLOWCHART_MODIFIABLE_ON_CREATION_DEFAULT,
      isFlowchartModifiableAfterCreation: IS_FLOWCHART_MODIFIABLE_AFTER_CREATION_DEFAULT,
    },
  };
  return {staticFlowchart, metadata};
}

function buildReadOnlyTaskDefinitionVersionSpec(
  node: TaskSchemaViewerNodeData
): TaskDefinitionVersionSpecWithTaskSchema {
  const processTaskDefinitionVersion = node.processTaskDefinitionVersion;
  const processTaskSchema =
    processTaskDefinitionVersion != null && !isInaccessible(processTaskDefinitionVersion.processTaskSchema)
      ? processTaskDefinitionVersion.processTaskSchema
      : null;
  return {
    clientId: processTaskDefinitionVersion?.id ?? node.id,
    currentVersionId: processTaskDefinitionVersion?.id ?? null,
    inputFieldDefinitions: [],
    outputFieldDefinitions: null,
    newProcessTaskDefinitionVersion: {
      taskSchemaId: processTaskSchema?.id ?? node.taskSchemaId,
      applicationData: null,
      taskSchema: {
        id: processTaskSchema?.id ?? node.taskSchemaId,
        name: processTaskSchema?.name ?? node.nodeName,
        executionType: processTaskSchema?.executionType ?? ProcessExecutionType.DIRECT,
        fields: [],
        outputSchema: null,
      },
    },
  };
}

function convertViewerNodeToStaticNode(node: TaskSchemaViewerNodeData): TaskSchemaStaticNodeData {
  return {
    ...node,
    taskDefinitionVersionSpec: buildReadOnlyTaskDefinitionVersionSpec(node),
    sections: [],
  };
}

export function convertViewerFlowchartToStatic(
  viewerFlowchart: ViewerStaticTemplateFlowchart
): StaticTemplateFlowchart {
  const nodesStaticData: Array<StaticFlowchartNodeData> = viewerFlowchart.nodesStaticData.map((node) => {
    if (node.nodeType === ProcessFlowchartNodeType.TASK_SCHEMA_NODE) {
      return convertViewerNodeToStaticNode(node);
    }
    return node;
  });
  return {nodesStaticData, edgesStaticData: viewerFlowchart.edgesStaticData};
}

function getTaskSchemaNodeIcon(
  nodeDetailsType: ProcessFlowchartNodeDetailsType
): typeof STANDARD_ROOT_NODE_ICON | typeof STANDARD_END_NODE_ICON | typeof TASK_SCHEMA_NODE_ICON {
  switch (nodeDetailsType) {
    case ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS:
      return STANDARD_ROOT_NODE_ICON;
    case ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS:
      return STANDARD_END_NODE_ICON;
    default:
      return TASK_SCHEMA_NODE_ICON;
  }
}

function buildTaskSchemaNode(
  nodeConfig: NodeConfig,
  nodeDetailsType: ProcessFlowchartNodeDetailsType,
  schemaId: string,
  schemaName: string,
  processTaskDefinitionVersion?: ReadOnlyProcessTaskDefinitionVersion,
  statusCounts?: Array<{statusType: ProcessTaskStatusType; displayName: string; count: number}>
): TaskSchemaViewerNodeData {
  return {
    id: nodeConfig.id,
    nodeType: ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
    nodeDetailsType,
    nodeName: nodeConfig.name || schemaName,
    nodeIcon: getTaskSchemaNodeIcon(nodeDetailsType),
    taskSchemaId: schemaId,
    processTaskDefinitionVersion,
    statusCounts,
  };
}

function buildNodeFromConfig(
  nodeConfig: NodeConfig,
  statusCountsByNodeConfigId: Record<
    string,
    Array<{statusType: ProcessTaskStatusType; displayName: string; count: number}>
  >
): ViewerStaticFlowchartNodeData | null {
  const {nodeDetails} = nodeConfig;
  const statusCounts = statusCountsByNodeConfigId[nodeConfig.id];
  switch (nodeDetails.__typename) {
    case 'ProcessRootNodeDetailsNode': {
      const schemaId = !isInaccessible(nodeDetails.taskSchema) ? nodeDetails.taskSchema.id : 'unknown';
      const schemaName = !isInaccessible(nodeDetails.taskSchema) ? nodeDetails.taskSchema.name : 'Start';
      const processTaskDefinitionVersion = !isInaccessible(nodeDetails.processTaskDefinitionVersion)
        ? nodeDetails.processTaskDefinitionVersion
        : undefined;
      return buildTaskSchemaNode(
        nodeConfig,
        nodeDetails.type,
        schemaId,
        schemaName,
        processTaskDefinitionVersion,
        statusCounts
      );
    }
    case 'ProcessTaskNodeDetailsNode': {
      const schemaId = !isInaccessible(nodeDetails.schema) ? nodeDetails.schema.id : 'unknown';
      const schemaName = !isInaccessible(nodeDetails.schema) ? nodeDetails.schema.name : 'Task';
      const processTaskDefinitionVersion = !isInaccessible(nodeDetails.processTaskDefinitionVersion)
        ? nodeDetails.processTaskDefinitionVersion
        : undefined;
      return buildTaskSchemaNode(
        nodeConfig,
        nodeDetails.type,
        schemaId,
        schemaName,
        processTaskDefinitionVersion,
        statusCounts
      );
    }
    case 'ProcessOutputNodeDetailsNode': {
      const schemaId = !isInaccessible(nodeDetails.outputSchema)
        ? nodeDetails.outputSchema.taskSchema.id
        : 'unknown';
      const schemaName = !isInaccessible(nodeDetails.outputSchema)
        ? nodeDetails.outputSchema.taskSchema.name
        : 'Output';
      const processTaskDefinitionVersion = !isInaccessible(nodeDetails.processTaskDefinitionVersion)
        ? nodeDetails.processTaskDefinitionVersion
        : undefined;
      return buildTaskSchemaNode(
        nodeConfig,
        nodeDetails.type,
        schemaId,
        schemaName,
        processTaskDefinitionVersion,
        statusCounts
      );
    }
    case 'ProcessRouterNodeDetailsNode':
      return {
        id: nodeConfig.id,
        nodeType: ProcessFlowchartNodeType.ROUTER_NODE,
        nodeDetailsType: ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS,
        nodeName: nodeDetails.name,
        nodeIcon: getLogicNodeMetadata(ProcessFlowchartNodeType.ROUTER_NODE).icon,
        routerConfig: {
          clientId: nodeConfig.id,
          name: nodeDetails.name,
          functionSpecs: [],
          defaultFunctionSpec: null,
        },
      } satisfies RouterStaticNodeData;
    case 'ProcessEndNodeDetailsNode':
      return {
        id: nodeConfig.id,
        nodeType: ProcessFlowchartNodeType.STOP_NODE,
        nodeDetailsType: ProcessFlowchartNodeDetailsType.END_NODE_DETAILS,
        nodeName: nodeDetails.name,
        nodeIcon: getLogicNodeMetadata(ProcessFlowchartNodeType.STOP_NODE).icon,
      } as EndStaticNodeData;
    default:
      return null;
  }
}

function buildEdgeLabelMap(nodeConfigs: FlowchartStructure['nodeConfigs']): Record<string, string> {
  const labelMap: Record<string, string> = {};
  for (const nodeConfig of nodeConfigs) {
    if (nodeConfig.nodeDetails.__typename !== 'ProcessRouterNodeDetailsNode') {
      continue;
    }
    for (const fn of nodeConfig.nodeDetails.routerFunctions) {
      labelMap[fn.edgeConfig.id] = fn.name;
    }
  }
  return labelMap;
}

function buildEdgeFromConfig(
  edgeConfig: FlowchartStructure['edgeConfigs'][number],
  edgeLabelMap: Record<string, string>
): FlowchartStaticEdgeData {
  const sourceId = edgeConfig.fromNodeConfig.id;
  const targetId = edgeConfig.toNodeConfig.id;
  return {
    id: edgeConfig.id,
    source: sourceId,
    target: targetId,
    sourceHandle: `${sourceId}-source`,
    targetHandle: `${targetId}-target`,
    type: ProcessFlowchartEdgeType.CONTROL_FLOW,
    label: edgeLabelMap[edgeConfig.id],
  };
}

function buildLineageMaps(flowchartProcess: FlowchartProcess): {
  statusCountsByNodeConfigId: Record<
    string,
    Array<{statusType: ProcessTaskStatusType; displayName: string; count: number}>
  >;
  processIdsByNodeConfigId: Record<string, string>;
  errorsByNodeConfigId: Record<string, Array<string>>;
} {
  const statusCountsByNodeConfigId: Record<
    string,
    Array<{statusType: ProcessTaskStatusType; displayName: string; count: number}>
  > = {};
  const processIdsByNodeConfigId: Record<string, string> = {};
  const errorsByNodeConfigId: Record<string, Array<string>> = {};

  const allProcesses = [flowchartProcess, ...flowchartProcess.childProcesses];
  for (const process of allProcesses) {
    if (isInaccessible(process) || process.nodeConfig == null) {
      continue;
    }
    if (process.hasTasksWithFailedMappings) {
      errorsByNodeConfigId[process.nodeConfig.id] = ['Fix mapping errors to continue this workflow.'];
    }
    if (process.taskStatusCounts.length === 0) {
      continue;
    }
    statusCountsByNodeConfigId[process.nodeConfig.id] = process.taskStatusCounts.map((sc) => ({
      statusType: sc.status.statusType,
      displayName: sc.status.displayName,
      count: sc.count,
    }));
    processIdsByNodeConfigId[process.nodeConfig.id] = process.id;
  }

  return {statusCountsByNodeConfigId, processIdsByNodeConfigId, errorsByNodeConfigId};
}

/**
 * Builds a lightweight, read-only flowchart from the GraphQL structure query.
 * Used by the execution viewer to render the flowchart without loading the full
 * config editor machinery. Optionally merges status counts from lineage data.
 */
export default function buildDisplayOnlyDraftFromStructure(
  flowchartStructure: FlowchartStructure,
  flowchartProcess?: FlowchartProcess
): DisplayOnlyFlowchartResult {
  const {statusCountsByNodeConfigId, processIdsByNodeConfigId, errorsByNodeConfigId} =
    flowchartProcess != null
      ? buildLineageMaps(flowchartProcess)
      : {statusCountsByNodeConfigId: {}, processIdsByNodeConfigId: {}, errorsByNodeConfigId: {}};

  const nodesStaticData: Array<ViewerStaticFlowchartNodeData> = [];
  for (const nodeConfig of flowchartStructure.nodeConfigs) {
    const node = buildNodeFromConfig(nodeConfig, statusCountsByNodeConfigId);
    if (node != null) {
      const errors = errorsByNodeConfigId[nodeConfig.id];
      if (errors != null && node.nodeType === ProcessFlowchartNodeType.TASK_SCHEMA_NODE) {
        node.errors = errors;
      }
      nodesStaticData.push(node);
    }
  }

  const edgeLabelMap = buildEdgeLabelMap(flowchartStructure.nodeConfigs);
  const edgesStaticData: Array<FlowchartStaticEdgeData> = flowchartStructure.edgeConfigs.map((edge) =>
    buildEdgeFromConfig(edge, edgeLabelMap)
  );

  return {
    flowchart: {nodesStaticData, edgesStaticData},
    processIdsByNodeConfigId,
  };
}
